/* External dependencies */
import { useDispatch } from 'react-redux';
import { applyMiddleware, combineReducers, createStore as createReduxStore, Dispatch, Store } from 'redux';
import { combineEpics, createEpicMiddleware } from 'redux-observable';

/* Local dependencies */
import { ForgotPasswordAction } from '../components/dashboard/auth/forgot-password/redux/actions';
import { confirmForgotPasswordEpic, forgotPasswordEpic } from '../components/dashboard/auth/forgot-password/redux/epic';
import forgotPasswordReducer from '../components/dashboard/auth/forgot-password/redux/reducer';
import { LoginAction } from '../components/dashboard/auth/login/redux/actions';
import { loginEpic } from '../components/dashboard/auth/login/redux/epic';
import loginReducer from '../components/dashboard/auth/login/redux/reducer';
import { AuthAction } from '../components/dashboard/auth/redux/auth-actions';
import {
  confirmRegistrationEpic,
  initClientEpic,
  logoutEpic,
  sendVerificationCodeEpic,
} from '../components/dashboard/auth/redux/auth-epics';
import authReducer from '../components/dashboard/auth/redux/auth-reducer';
import { RegisterAction } from '../components/dashboard/auth/register/redux/actions';
import { registerEpic } from '../components/dashboard/auth/register/redux/epic';
import registerReducer from '../components/dashboard/auth/register/redux/reducer';
import createOrderEpic from '../components/dashboard/orders/create-order/redux/epic';
import createOrderReducer from '../components/dashboard/orders/create-order/redux/reducer';
import { CreateOrderAction } from '../components/dashboard/orders/create-order/redux/types';
import listOrdersEpic from '../components/dashboard/orders/list-orders/redux/epic';
import listOrdersReducer from '../components/dashboard/orders/list-orders/redux/reducer';
import { ListOrdersAction } from '../components/dashboard/orders/list-orders/redux/types';
import updateOrderEpic from '../components/dashboard/orders/update-order/redux/epic';
import updateOrderReducer from '../components/dashboard/orders/update-order/redux/reducer';
import { UpdateOrderAction } from '../components/dashboard/orders/update-order/redux/types';
import createWarehouseEpic from '../components/dashboard/warehouses/create-warehouse/redux/epic';
import createWarehouseReducer from '../components/dashboard/warehouses/create-warehouse/redux/reducer';
import { CreateWarehouseAction } from '../components/dashboard/warehouses/create-warehouse/redux/types';
import getWarehouseEpic from '../components/dashboard/warehouses/get-warehouse/redux/epic';
import getWarehouseReducer from '../components/dashboard/warehouses/get-warehouse/redux/reducer';
import { GetWarehouseAction } from '../components/dashboard/warehouses/get-warehouse/redux/types';
import listWarehousesEpic from '../components/dashboard/warehouses/list-warehouses/redux/epic';
import listWarehousesReducer from '../components/dashboard/warehouses/list-warehouses/redux/reducer';
import { ListWarehousesAction } from '../components/dashboard/warehouses/list-warehouses/redux/types';
import updateWarehouseEpic from '../components/dashboard/warehouses/update-warehouse/redux/epic';
import updateWarehouseReducer from '../components/dashboard/warehouses/update-warehouse/redux/reducer';
import { UpdateWarehouseAction } from '../components/dashboard/warehouses/update-warehouse/redux/types';
import getUserEpic from '../components/dashboard/users/get-user/redux/epic';
import getUserReducer from '../components/dashboard/users/get-user/redux/reducer';
import listUsersEpic from '../components/dashboard/users/list-users/redux/epic';
import listUsersReducer from '../components/dashboard/users/list-users/redux/reducer';
import { ListUsersAction } from '../components/dashboard/users/list-users/redux/types';
import updateProfileEpic from '../components/dashboard/users/update-profile/redux/epic';
import { updateProfileReducer } from '../components/dashboard/users/update-profile/redux/reducer';
import { UpdateProfileActions } from '../components/dashboard/users/update-profile/redux/types';
import updateUserForAdminEpic from '../components/dashboard/users/update-user-for-admin/redux/epic';
import updateUserForAdminReducer from '../components/dashboard/users/update-user-for-admin/redux/reducer';
import { UpdateUserForAdminAction } from '../components/dashboard/users/update-user-for-admin/redux/types';
import updateUserForManagerEpic from '../components/dashboard/users/update-user-for-manager/redux/epic';
import updateUserForManagerReducer from '../components/dashboard/users/update-user-for-manager/redux/reducer';
import signedUrlEpic from '../components/media/redux/epic';
import { signedUrlReducer } from '../components/media/redux/reducer';
import { SignedUrlAction } from '../components/media/redux/type';

const appReducer = (state = {}, action: any) => {
  return state;
};

const rootEpic = combineEpics(
  initClientEpic,
  sendVerificationCodeEpic,
  logoutEpic,
  confirmRegistrationEpic,
  loginEpic,
  registerEpic,
  forgotPasswordEpic,
  confirmForgotPasswordEpic,
  listOrdersEpic,
  createOrderEpic,
  updateOrderEpic,
  getUserEpic,
  listUsersEpic,
  updateUserForAdminEpic,
  updateUserForManagerEpic,
  updateProfileEpic,
  listWarehousesEpic,
  signedUrlEpic,
  getWarehouseEpic,
  createWarehouseEpic,
  updateWarehouseEpic,
);

const rootReducer = combineReducers({
  app: appReducer,
  auth: authReducer,
  login: loginReducer,
  register: registerReducer,
  forgotPassword: forgotPasswordReducer,
  listOrders: listOrdersReducer,
  createOrder: createOrderReducer,
  updateOrder: updateOrderReducer,
  getUser: getUserReducer,
  listUsers: listUsersReducer,
  updateUserForAdmin: updateUserForAdminReducer,
  updateUserForManager: updateUserForManagerReducer,
  updateProfile: updateProfileReducer,
  listWarehouses: listWarehousesReducer,
  signedUrl: signedUrlReducer,
  getWarehouse: getWarehouseReducer,
  createWarehouse: createWarehouseReducer,
  updateWarehouse: updateWarehouseReducer,
});

let store: Store;

export function createStore(): Store {
  const epicMiddleware = createEpicMiddleware();
  store = createReduxStore(rootReducer, applyMiddleware(epicMiddleware));

  epicMiddleware.run(rootEpic);

  return store;
}

export type RootState = ReturnType<typeof store.getState>;

export type AppAction =
  | AuthAction
  | CreateOrderAction
  | ForgotPasswordAction
  | ListOrdersAction
  | ListUsersAction
  | ListWarehousesAction
  | LoginAction
  | RegisterAction
  | SignedUrlAction
  | UpdateOrderAction
  | UpdateUserForAdminAction
  | UpdateProfileActions
  | GetWarehouseAction
  | CreateWarehouseAction
  | UpdateWarehouseAction;

export type AppDispatch = Dispatch<AppAction>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
